// useCinematicStore.js - Simplified cinematic state management
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import AnimationController from '@components/game/effects/AnimationController';
import useGameStore from '@store/useGameStore';
import { calculateMSBasedOnTicks } from '@utils/animationTiming';

// Constant for uninitialized state
export const CINEMATIC_NOT_STARTED = -1;

// LocalStorage key for persisting playback speed
const PLAYBACK_SPEED_KEY = 'xhelos_cinematic_playback_speed';

/**
 * Load playback speed from localStorage
 * @returns {number} Saved playback speed or 1 (default)
 */
const loadPlaybackSpeed = () => {
  try {
    const saved = localStorage.getItem(PLAYBACK_SPEED_KEY);
    if (saved) {
      const speed = parseFloat(saved);
      // Validate the saved value
      if (!isNaN(speed) && speed > 0 && speed <= 10) {
        return speed;
      }
    }
  } catch (error) {
    console.warn('[useCinematicStore] Failed to load playback speed from localStorage:', error);
  }
  return 1; // Default speed
};

/**
 * Save playback speed to localStorage
 * @param {number} speed - The speed to save
 */
const savePlaybackSpeed = (speed) => {
  try {
    localStorage.setItem(PLAYBACK_SPEED_KEY, speed.toString());
  } catch (error) {
    console.warn('[useCinematicStore] Failed to save playback speed to localStorage:', error);
  }
};

const useCinematicStore = create(
  subscribeWithSelector((set, get) => ({
  // --- Core State ---
  currentActionCardIndex: 0,        // Which action card is currently selected in the UI
  executedResolutionQueueIndex: CINEMATIC_NOT_STARTED,  // Which RQ items have been executed (-1 = none)
  isAnimating: false,               // Whether we're currently playing an animation
  animationControllerRef: null,     // Reference to current AnimationController
  playbackSpeed: loadPlaybackSpeed(),  // Animation playback speed multiplier (loaded from localStorage)
  isAutoPlay: false,                // Whether auto-play is enabled
  
  // --- Reset ---
  reset: () => {
    console.warn('[useCinematicStore] Resetting...');
    const state = get();

    // Cancel any running animation
    if (state.animationControllerRef) {
      state.animationControllerRef.cancel();
    }

    set({
      currentActionCardIndex: 0,
      executedResolutionQueueIndex: CINEMATIC_NOT_STARTED,
      isAnimating: false,
      animationControllerRef: null,
      playbackSpeed: loadPlaybackSpeed(), // Preserve user's preferred speed
      isAutoPlay: false // Stop auto-play on reset
    });
    // console.log('[useCinematicStore] AUTO-PLAY DISABLED: reset() was called');
  },
  
  // --- Select Card ---
  selectCard: (index, fromAutoPlay = false) => {
    const state = get();
    
    // console.log('[useCinematicStore] selectCard called. index:', index, 'fromAutoPlay:', fromAutoPlay, 'isAutoPlay:', state.isAutoPlay);
    
    // Guard: If already at this card, do nothing
    if (state.currentActionCardIndex === index) {
      return;
    }
    
    // Cancel any running animation when selecting a different card
    if (state.animationControllerRef) {
      state.animationControllerRef.cancel();
    }
    
    // Stop auto-play only when user manually selects a card (not from auto-play itself)
    if (state.isAutoPlay && !fromAutoPlay) {
      // console.log('[useCinematicStore] AUTO-PLAY DISABLED: User manually selected card', index);
      set({ isAutoPlay: false });
    }

    // Determine the executed index based on which card is selected
    const gameStore = useGameStore.getState();
    const maxSteps = gameStore.game?.resolution_queue?.length || 0;
    
    let newExecutedIndex;
    if (index >= maxSteps) {
      // Special case: selecting the "End" card - show the final state (all actions executed)
      newExecutedIndex = maxSteps - 1;
    } else {
      // Normal case: show the state BEFORE the selected card's action executes
      newExecutedIndex = index - 1; // -1 for card 0 (shows base), 0 for card 1, etc.
    }
    
    set({ 
      currentActionCardIndex: index,
      executedResolutionQueueIndex: newExecutedIndex,
      isAnimating: false, // Stop any ongoing animation
      animationControllerRef: null // Clear the reference
    });
  },
  
  // --- Execute Card ---
  executeCard: () => {
    const state = get();

    //console.info('[useCinematicStore] Starting card execution! Card index:', state.currentActionCardIndex, 'at time:', Date.now());

    // Validate we can execute
    if (state.isAnimating) {
      console.warn('[useCinematicStore] Already animating, ignoring execute request');
      return;
    }
    
    if (state.currentActionCardIndex <= state.executedResolutionQueueIndex) {
      console.warn('[useCinematicStore] Card already executed or invalid index');
      return;
    }
    
    // Cancel any existing animation (safety check)
    if (state.animationControllerRef) {
      state.animationControllerRef.cancel();
    }
    
    // Get visual effects for current card
    const rqItem = useGameStore.getState().getResolutionQueueItem?.(state.currentActionCardIndex);
    if (!rqItem) {
      console.warn('[useCinematicStore] No resolution queue item at index:', state.currentActionCardIndex);
      // Still mark as executed to avoid getting stuck
      //console.log('[useCinematicStore] Setting isAnimating to FALSE (no rq item found)');
      set({ 
        executedResolutionQueueIndex: state.currentActionCardIndex,
        isAnimating: false 
      });
      return;
    }
    
    // Create new animation controller - pass the whole rqItem for full context
    const controller = new AnimationController(rqItem, () => {
      get().completeAnimation();
    });
    
    //console.log('[useCinematicStore] Setting isAnimating to TRUE for card index:', state.currentActionCardIndex);
    set({ 
      isAnimating: true,
      animationControllerRef: controller 
    });
    
    // Start the animation
    controller.start();
  },
  
  // --- Set Playback Speed ---
  setPlaybackSpeed: (speed) => {
    // Validate speed
    if (typeof speed !== 'number' || speed <= 0 || speed > 10) {
      console.warn('[useCinematicStore] Invalid playback speed:', speed);
      return;
    }

    // Save to localStorage for persistence
    savePlaybackSpeed(speed);

    set({ playbackSpeed: speed });
  },
  
  // --- Toggle Auto-play ---
  setAutoPlay: (enabled) => {
    // console.log('[useCinematicStore] setAutoPlay called with:', enabled);
    set({ isAutoPlay: enabled });
    
    // If enabling auto-play and not currently animating, start it
    if (enabled && !get().isAnimating) {
      const state = get();
      const gameStore = useGameStore.getState();
      const maxSteps = gameStore.game?.resolution_queue?.length || 0;
      
      // Check if we're at the end (all executed)
      if (state.executedResolutionQueueIndex >= maxSteps - 1) {
        // Reset to beginning - first select card 0 (pass true for fromAutoPlay!)
        get().selectCard(0, true);
        // Then execute after a brief delay for visual feedback
        setTimeout(() => {
          get().executeCard();
        }, 200);
      } else if (state.currentActionCardIndex > state.executedResolutionQueueIndex) {
        // Normal case - execute current card
        get().executeCard();
      }
    }
  },
  
  // --- Complete Animation (called by AnimationController) ---
  completeAnimation: () => {
    const state = get();

    //console.info('[useCinematicStore] Card animations completed! Card index:', state.currentActionCardIndex, 'at time:', Date.now());

    if (!state.isAnimating) {
      console.warn('[useCinematicStore] completeAnimation called but not animating!');
      return;
    }
    
    // Update executed index and clear animation state
    //console.log('[useCinematicStore] Setting isAnimating to FALSE after completing card index:', state.currentActionCardIndex);
    set({ 
      executedResolutionQueueIndex: state.currentActionCardIndex,
      isAnimating: false,
      animationControllerRef: null // Clear the reference
    });
    
    // Check if we should continue (auto-play or manual advance)
    const gameStore = useGameStore.getState();
    const maxSteps = gameStore.game?.resolution_queue?.length || 0;
    const nextIndex = state.currentActionCardIndex + 1;
    
    if (nextIndex < maxSteps) {
      // Advance to next card
      setTimeout(() => {
        // Pass true to indicate this is from auto-play flow
        get().selectCard(nextIndex, true);
        
        // If auto-play is on, execute the next card
        if (get().isAutoPlay) {
          setTimeout(() => {
            get().executeCard();
          }, calculateMSBasedOnTicks(1)); // 1 tick delay for visual feedback
        }
      }, calculateMSBasedOnTicks(4)); // 1 tick delay before advancing
    } else {
      // Reached the end - visually select the "End" card
      setTimeout(() => {
        // Set currentActionCardIndex to maxSteps to select the End card
        // Keep executedResolutionQueueIndex as is (shows correct snapshot)
        // console.log('[useCinematicStore] AUTO-PLAY DISABLED: Reached the end of sequence');
        set({ 
          currentActionCardIndex: maxSteps,
          isAutoPlay: false // Stop auto-play at the end
        });
      }, calculateMSBasedOnTicks(1));
    }
  }
})));

export default useCinematicStore;